import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ReadyDot, Price, Money, Photo, wt } from "./VariantChooser";
import { COLOR_NAME } from "./mcApi";

// CS 22.13 -- distributor-typed links: three stacked tiers.
const DistLine = ({ label, short, v, testid, accent }) => (
  <div className="flex items-baseline justify-between gap-2">
    <span className={`text-[9px] uppercase tracking-[0.12em] whitespace-nowrap ${accent ? "text-[#8a6d1f]" : "text-slate-400"}`}>
      <span className="sm:hidden">{short}</span><span className="hidden sm:inline">{label}</span>
    </span>
    <Price v={v} className={`text-[12.5px] ${accent ? "font-medium text-[#0D1B2A]" : ""}`} testid={testid} />
  </div>
);
const DistPrices = ({ c }) => (
  <>
    <DistLine label="Distribution cost" short="Dist. cost" v={c.price.franchise_total} testid={`mc-card-dist-${c.design_key}`} />
    <DistLine label="Retailer price" short="Retailer" v={c.price.retailer_total} testid={`mc-card-retailer-${c.design_key}`} />
    <DistLine label="Customer MRP" short="MRP" v={c.price.customer_total} testid={`mc-card-mrp-${c.design_key}`} accent />
  </>
);

// CS 22.14 -- wholesaler links: Wholesaler cost + B2B price only (no MRP).
const WholesaleRow = ({ label, accent, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0 sm:gap-2 min-w-0">
    <span className={`text-[9px] uppercase tracking-[0.12em] whitespace-nowrap ${accent ? "text-[#8a6d1f]" : "text-slate-400"}`}>{label}</span>
    <span className="min-w-0 truncate">{children}</span>
  </div>
);
const WholesalePrices = ({ c }) => (
  <>
    <WholesaleRow label="Wholesaler cost"><Money v={c.price.wholesaler_cost} cur={c.price.cost_currency} className="text-[12px] sm:text-[12.5px]" testid={`mc-card-wcost-${c.design_key}`} /></WholesaleRow>
    <WholesaleRow label="B2B price" accent>{c.price.b2b_price == null ? <span className="text-[11px] text-slate-400" data-testid={`mc-card-b2b-${c.design_key}`}>Not set</span> : <Money v={c.price.b2b_price} cur={c.price.selling_currency} className="text-[12px] sm:text-[12.5px] font-medium text-[#0D1B2A]" testid={`mc-card-b2b-${c.design_key}`} />}</WholesaleRow>
  </>
);

export const CatalogCard = ({ c, onOpen, admin, selected, onToggle }) => {
  const sv = c.shown_variant;
  return (
    <div className="relative h-full">
    {onToggle && (
      <div className="absolute top-2.5 left-2.5 z-10 rounded-md bg-white/90 p-1 shadow-sm" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={!!selected} onCheckedChange={() => onToggle(c.design_key)} aria-label={`Select ${c.design_key}`} data-testid={`mc-card-select-${c.design_key}`} />
      </div>
    )}
    <button onClick={() => onOpen(c)} data-testid={`mc-card-${c.design_key}`}
      className={`w-full text-left bg-white border rounded-xl ${selected ? "border-[#C9A84C] ring-1 ring-[#C9A84C]/50" : "border-[#0D1B2A]/[0.07]"} overflow-hidden flex flex-col h-full transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-18px_rgba(13,27,42,0.35)]`}>
      <div className="aspect-square p-4 border-b border-[#0D1B2A]/[0.05]">
        <Photo url={c.photo_url} size={c.photo_size} alt={c.design_key} />
      </div>
      <div className="p-3.5 flex flex-col gap-1 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] tracking-[0.06em] text-[#0D1B2A] font-medium truncate" data-testid={`mc-card-key-${c.design_key}`}>{c.design_key}</span>
          {c.ready && <ReadyDot testid={`mc-card-ready-${c.design_key}`} />}
        </div>
        <div className="text-[11px] text-slate-400 truncate">{c.category}{c.is_al ? " · Ashth Lite" : ""}</div>
        <div className="text-[11px] text-slate-600 truncate" data-testid={`mc-card-variant-${c.design_key}`}>
          <span className={sv.is_physical ? "text-[#8a6d1f]" : "text-slate-500"}>{sv.is_physical ? "Uploaded variant" : "Estimate"}</span>
          {" · "}{COLOR_NAME[sv.color] || "—"} {sv.purity || ""}
        </div>
        <div className="mt-auto pt-2">
          {c.price && (c.price.priceable ? (
            <div className="space-y-1 border-t border-[#0D1B2A]/[0.06] pt-2" data-testid={`mc-card-price-${c.design_key}`}>
              {"wholesaler_cost" in c.price ? <WholesalePrices c={c} /> : "retailer_total" in c.price ? <DistPrices c={c} /> : (<>
              <div className="flex items-baseline justify-between gap-2"><span className="text-[9px] uppercase tracking-[0.18em] text-slate-400">Franchise</span><Price v={c.price.franchise_total} className="text-[13px]" testid={`mc-card-franchise-${c.design_key}`} /></div>
              <div className="flex items-baseline justify-between gap-2"><span className="text-[9px] uppercase tracking-[0.18em] text-[#8a6d1f]">MRP</span><Price v={c.price.customer_total} className="text-[13px] font-medium text-[#0D1B2A]" testid={`mc-card-mrp-${c.design_key}`} /></div>
              </>)}
              <div className="text-[10px] text-slate-400 mc-num" data-testid={`mc-card-weight-${c.design_key}`}>{wt(c.price.converted_net_weight)} net gold</div>
            </div>
          ) : (
            <div className="text-[11px] text-red-600" data-testid={`mc-card-unpriceable-${c.design_key}`}>{c.price.unpriceable_reason === "diamond_not_in_price_list" ? "Price on request" : "Price unavailable — data review"}</div>
          ))}
          {admin && c.conflict_count > 0 && (
            <div className="text-[10px] text-amber-700 mt-1" data-testid={`mc-card-conflicts-${c.design_key}`}>{c.conflict_count} data note(s)</div>
          )}
        </div>
      </div>
    </button>
    </div>
  );
};
